import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import PeopleIcon from "@mui/icons-material/People";
import { IconButton, Tooltip } from "@mui/material";
import { UserCheck } from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchInternsss, fetchIntershipTypes } from "../../../Apis/InternManagement";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import InternConvertDialog from "./InternConvertDialog";
import { fetchOrganizationDepartment } from "../../../Apis/Department-api";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "intern_code",
  sortOrder: "desc",
};

const INTERN_TOOLBAR_FILTERS = [
  { urlKey: "department_id", field: "department" },
  {
    urlKey: "internship_type_id",
    field: "organization_internship_type_id",
  },
];

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "intern_code",
    label: "intern_code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "name",
    label: "name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department",
    label: "department",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "Functional_Role",
    label: "Functional_Role",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function InternList() {
  const [intern, setIntern] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [internshipTypes, setInternshipTypes] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [selectedIntern, setSelectedIntern] = useState(null);
  const { userData } = useAuthStore();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const org = userData?.organization;
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Interns_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const [deptRes, typeRes] = await Promise.allSettled([
          fetchOrganizationDepartment(org.organization_id),
          fetchIntershipTypes(org.organization_id),
        ]);
        if (cancelled) return;
        if (deptRes.status === "fulfilled") {
          setDepartments(asOptionList(deptRes.value, ["Departments", "data"]));
        }
        if (typeRes.status === "fulfilled") {
          setInternshipTypes(
            asOptionList(typeRes.value, ["intership", "data"])
          );
        }
      } catch (error) {
        console.error("Failed to load intern toolbar filters", error);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load interns data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          INTERN_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "intern_code",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/interns/filters`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          const oldData = await fetchInternsss(org.organization_id);
          let internsArray = Array.isArray(oldData)
            ? oldData
            : oldData?.data || oldData?.interns || [];
          if (!Array.isArray(internsArray)) internsArray = [];
          const activeInterns = applyClientToolbarFilters(
            internsArray.filter(
              (item) =>
                item?.Internship_Status !== "Exited" &&
                item?.Internship_Status !== "exited"
            ),
            searchParams,
            INTERN_TOOLBAR_FILTERS
          );
          data = activeInterns.map((item) => ({
            ...item,
            id: item.organization_person_id ?? item.id,
            name: item.name ?? item.full_name ?? item.Intern_Name,
            intern_code: item.intern_code ?? item.person_code ?? item.Intern_Code,
            department: item.department ?? item.Department ?? item.department_name,
            functional_role:
              item.functional_role ?? item.designation ?? item.Functional_Role ?? "",
              
          }));
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const normalized = (Array.isArray(data) ? data : [])
          .filter((item) => {
            const status = String(
              item?.Internship_Status ?? item?.internship_status ?? ""
            ).toLowerCase();
            if (item?.is_converted_to_employee) return true;
            return !status.includes("exit");
          })
          .map((item) => {
          const raw = item?.profile_image_url;
          let profile_image_url = "";
          if (raw && typeof raw === "string") {
            if (raw.startsWith("http://") || raw.startsWith("https://")) {
              // Backend already sent a full URL
              profile_image_url = raw;
            } else if (raw.startsWith("interns/") || raw.startsWith("persons/") || raw.startsWith("employees/")) {
              // Raw is a relative path including its folder; just prepend storage base once
              profile_image_url = `${MAIN_URL}/storage/app/public/${raw}`;
            } else {
              // Backward-compat: treat as a filename for interns
              profile_image_url = `${MAIN_URL}/storage/app/public/interns/${raw}`;
            }
          }
          return {
            ...item,
            id: item.organization_person_id ?? item.id,
            name: item.name ?? item.full_name ?? item.Intern_Name ?? item.Name ?? "",
            intern_code: item.intern_code ?? item.person_code ?? item.Intern_Code ?? "",
            department: item.department ?? item.Department ?? item.department_name ?? "",
            functional_role:
              item.functional_role ?? item.designation ?? item.Functional_Role ?? "",
            profile_image_url,
            internship_start_date: item.internship_start_date ?? item.Internship_Start_Date ?? item.date_of_joining ?? null,
            internship_end_date: item.internship_end_date ?? item.Internship_End_Date ?? null,
            date_of_joining: item.date_of_joining ?? item.Internship_Start_Date ?? item.internship_start_date ?? null,
            work_shift_name: item.work_shift_name ?? item.work_shift ?? item.Work_Shift ?? "",
            work_shift: item.work_shift ?? item.work_shift_name ?? item.Work_Shift ?? "",
            intern_ship_duration: `${item.intern_ship_duration} months`,
            is_converted_to_employee: Boolean(
              item.is_converted_to_employee ?? item.can_convert_to_employee === false
            ),
            Internship_Status:
              item.Internship_Status ?? item.internship_status ?? "",
          };
        });

        // Keep footer total in sync when client-side status filter drops rows
        // (e.g. API still includes Exited-on-notice, list hides them).
        const rawCount = Array.isArray(data) ? data.length : 0;
        if (normalized.length !== rawCount) {
          const dropped = rawCount - normalized.length;
          const pageSize =
            Number.parseInt(searchParams.get("pageSize"), 10) ||
            paginationData.pageSize ||
            10;
          setPaginationData((prev) => {
            const newTotal = Math.max(0, (prev.total || rawCount) - dropped);
            const totalPages = Math.max(1, Math.ceil(newTotal / pageSize) || 1);
            const page = Math.min(prev.page || 1, totalPages);
            return {
              ...prev,
              page,
              total: newTotal,
              totalPages,
              hasNext: page < totalPages,
              hasPrev: page > 1,
            };
          });
        }

        setIntern(normalized);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching interns:", error);
        toast.error("Failed to load interns");
        setIntern([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org?.organization_id;
        if (!org_id) return;

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/interns/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Intern deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Intern";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Something went wrong. Please try again later.");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      const id = item.organization_person_id ?? item.id;
      navigate(`/organization/intern/intern-details/edit/${id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      const id = item.organization_person_id ?? item.id;
      navigate(`/organization/intern/intern-details/view/${id}`);
    },
    [navigate]
  );

  const handleConvert = useCallback((item) => {
    setSelectedIntern(item);
    setConvertOpen(true);
  }, []);

  const handleConverted = useCallback(() => {
    setSearchParams((prev) => new URLSearchParams(prev));
  }, [setSearchParams]);

  const additionalActions = useCallback(
    (row) => {
      const isConverted = Boolean(
        row?.is_converted_to_employee ||
          row?.can_convert_to_employee === false ||
          /converted/i.test(String(row?.Internship_Status || row?.internship_status || ""))
      );

      return (
        <Tooltip
          title={
            isConverted
              ? "Already converted to employee"
              : "Convert to Employee"
          }
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <IconButton
              size="small"
              disabled={isConverted}
              onClick={(e) => {
                e.stopPropagation();
                if (!isConverted) handleConvert(row);
              }}
              sx={{
                color: "var(--ems-muted)",
                opacity: isConverted ? 0.4 : 1,
                "&:hover": {
                  color: isConverted
                    ? "var(--ems-muted)"
                    : "var(--ems-primary)",
                  backgroundColor: isConverted
                    ? "transparent"
                    : "var(--ems-primary-tint-2)",
                },
              }}
            >
              <UserCheck size={16} />
            </IconButton>
          </span>
        </Tooltip>
      );
    },
    [handleConvert]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const carddata = useMemo(
    () => [
      { key: "profile_image_url", type: "photo" },
      { key: "name", type: "String" },
      { key: "functional_role", type: "String" },
    ],
    []
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading="Interns"
        btnName="Add Intern"
        delete_action="INTERN_DELETE"
        Data={intern}
        tableHeaders={[
          { name: "Code", value_key: "intern_code", width: "80px" },
          { name: "Name", value_key: "name", width: "150px" },
          { name: "Department", value_key: "department", width: "120px" },
          { name: "Functional Role", value_key: "functional_role", width: "150px" },
        ]}
        Icons={[
          <ExitToAppIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <ViewHeadlineIcon color="primary" />,
          <PeopleIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Intern details",
          "Intern details",
          "Add Intern",
          "Deleting this intern will remove their entire data from this organization permanently.",
        ]}
        Route="/organization/intern/intern-details"
        setData={setIntern}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="Interns"
          primaryKey="organization_person_id"
          heading="Interns"
          data={intern}
          sortname="intern_code"
          showActions={true}
          CardData={carddata}
          Route="/organization/intern/intern-details"
          DeleteFunc={handleDelete}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          edit_delete_action={["INTERN_DELETE", "INTERN_EDIT"]}
          EditFunc={handleEdit}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="interns"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Department"
                urlKey="department_id"
                allLabel="All departments"
                options={departments}
                valueKey="organization_department_id"
                labelKey="department_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Internship Type"
                urlKey="internship_type_id"
                allLabel="All internship types"
                options={internshipTypes}
                valueKey="organization_internship_type_id"
                labelKey="internship_type_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          additionalActions={additionalActions}
          {...(tableConfig && { config: tableConfig })}
        />
      )}

      <InternConvertDialog
        open={convertOpen}
        onClose={() => {
          setConvertOpen(false);
          setSelectedIntern(null);
        }}
        internRow={selectedIntern}
        orgId={org?.organization_id}
        onConverted={handleConverted}
      />
    </>
  );
}

export default InternList;
